import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faXmark, faSpinner, faWandMagicSparkles, faBoxOpen, faCircleInfo,
} from '@fortawesome/free-solid-svg-icons';
import { TextField, Switch } from '@mui/material';
import { useTranslation } from 'react-i18next';
import './ProductAnalyzerModal.css';

// Product Analyzer confirm step. After files upload, the backend
// (/api/analyze_products) returns a DRAFT schema per catalog: a one-line summary,
// a suggested product category, and an AI-written description per column. This
// modal lets the owner review/edit all of it and pick which fields matter, then
// hands the confirmed schemas to ingest. The category is the structural field
// that makes "which phone brands" / "find an Apple phone" answerable.

// MUI input style - copied from the account settings page (Administration.js) so
// every field here matches the rest of the portal exactly.
const inputSx = {
  '& .MuiOutlinedInput-root': { borderRadius: '10px' },   // 10px radius guideline
  '& .MuiOutlinedInput-root.Mui-focused': {
    '& fieldset': { border: '1px solid #515151' },
  },
  '& .MuiInputLabel-root': {
    color: 'gray',
    '&.Mui-focused': { color: '#5616ea' },
  },
};

// Include toggle — styled exactly like the deploy → Design subsection Switch
// (widgetCustomizationTab.js), so toggles look the same across the portal.
const switchSx = {
  '& .MuiSwitch-switchBase.Mui-checked': { color: '#5616ea' },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#5616ea' },
  '& .MuiSwitch-track': { backgroundColor: '#ccc' },
};

// 'storage_gb' -> 'Storage Gb' fallback if the analyzer gave no display name.
const prettify = (k) => String(k || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const ProductAnalyzerModal = ({ open, loading, catalogs, onClose, onConfirm }) => {
  const { t } = useTranslation();
  const [cats, setCats] = useState([]);
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (Array.isArray(catalogs)) {
      setCats(catalogs.map((c) => ({ ...c, columns: (c.columns || []).map((col) => ({ ...col })) })));
      setActive(0);
    }
  }, [catalogs]);

  if (!open) return null;

  const updateCat = (i, patch) => setCats((prev) => prev.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  const updateCol = (i, j, patch) =>
    setCats((prev) =>
      prev.map((c, idx) =>
        idx === i ? { ...c, columns: c.columns.map((col, k) => (k === j ? { ...col, ...patch } : col)) } : c
      )
    );

  const confirm = () => {
    const schemas = {};
    cats.forEach((c) => {
      if (!c.url || c.error) return;  // never import a catalog that yielded nothing
      schemas[c.url] = {
        summary: (c.summary || '').trim(),
        category: (c.category || '').trim(),
        columns: (c.columns || []).map((col) => ({
          key: col.key,
          display: (col.display || '').trim(),
          description: (col.description || '').trim(),
          selected: col.selected !== false,
        })),
      };
    });
    onConfirm(schemas);
  };

  const cat = cats[active];
  const importable = cats.filter((c) => c.url && !c.error && (c.columns?.length || c.row_count));

  return (
    <div className="pa-modal">
      <div className="pa-overlay" onClick={loading ? undefined : onClose} />
      <div className="pa-content">
        {/* Header - styled identically to chatModal.js (chat-modal-header). */}
        <div className="pa-header">
          <div className="pa-header-left">
            <span className="pa-header-ic"><FontAwesomeIcon icon={faWandMagicSparkles} /></span>
            <span className="pa-header-text">
              <span className="pa-title">{t('pa_title')}</span>
              <span className="pa-subtitle">{t('pa_subtitle')}</span>
            </span>
          </div>
          <button className="pa-close" onClick={onClose} disabled={loading} aria-label={t('close')}>
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        {loading ? (
          <div className="pa-loading">
            <FontAwesomeIcon icon={faSpinner} spin className="pa-spinner" />
            <p>{t('pa_analyzing')}</p>
          </div>
        ) : cats.length === 0 ? (
          <div className="pa-loading">
            <p>{t('pa_none')}</p>
          </div>
        ) : (
          <>
            {cats.length > 1 && (
              <div className="pa-tabs">
                {cats.map((c, i) => (
                  <button
                    key={c.url || i}
                    className={`pa-tab${i === active ? ' pa-tab-active' : ''}`}
                    onClick={() => setActive(i)}
                    title={c.filename}
                  >
                    <FontAwesomeIcon icon={faBoxOpen} />
                    <span className="pa-tab-label">{c.filename || t('pa_file_n', { n: i + 1 })}</span>
                  </button>
                ))}
              </div>
            )}

            <div className="pa-body">
              {cat && (
                <>
                  {cat.row_count > 0 && (
                    <div className="pa-count">{cat.row_count} {t('products').toLowerCase()}</div>
                  )}

                  {cat.error ? (
                    <div className="pa-error">
                      <FontAwesomeIcon icon={faCircleInfo} /> {t('pa_file_error')}
                    </div>
                  ) : (
                    <>
                      <div className="pa-section-head">
                        <span className="pa-section-title">{t('pa_overview_title')}</span>
                        <span className="pa-section-sub">{t('pa_overview_sub')}</span>
                      </div>

                      <div className="pa-field">
                        <TextField
                          label={t('pa_category')}
                          value={cat.category || ''}
                          onChange={(e) => updateCat(active, { category: e.target.value })}
                          placeholder={t('pa_category_ph')}
                          helperText={t('pa_category_hint')}
                          fullWidth
                          variant="outlined"
                          size="small"
                          sx={inputSx}
                        />
                      </div>

                      <div className="pa-field">
                        <TextField
                          label={t('pa_summary')}
                          value={cat.summary || ''}
                          onChange={(e) => updateCat(active, { summary: e.target.value })}
                          placeholder={t('pa_summary_ph')}
                          fullWidth
                          variant="outlined"
                          size="small"
                          multiline
                          minRows={2}
                          sx={inputSx}
                        />
                      </div>

                      <div className="pa-section-head pa-section-head-fields">
                        <span className="pa-section-title">{t('pa_fields')}</span>
                        <span className="pa-section-sub">{t('pa_fields_hint')}</span>
                      </div>

                      <div className="pa-cols">
                        {cat.columns.map((col, j) => (
                          <div key={col.key} className={`pa-col${col.selected === false ? ' pa-col-off' : ''}`}>
                            <div className="pa-col-top">
                              <Switch
                                checked={col.selected !== false}
                                onChange={(e) => updateCol(active, j, { selected: e.target.checked })}
                                title={t('pa_include')}
                                sx={switchSx}
                              />
                              <span className="pa-col-name">{col.display || prettify(col.key)}</span>
                              <code className="pa-col-key">{col.key}</code>
                              <span className={`pa-col-type pa-col-type-${col.type}`}>{col.type}</span>
                              {Array.isArray(col.samples) && col.samples.length > 0 && (
                                <span className="pa-col-samples">{t('pa_examples')} {col.samples.slice(0, 3).join(', ')}</span>
                              )}
                            </div>
                            <TextField
                              label={t('pa_field_desc')}
                              value={col.description || ''}
                              onChange={(e) => updateCol(active, j, { description: e.target.value })}
                              placeholder={t('pa_desc_ph')}
                              fullWidth
                              variant="outlined"
                              size="small"
                              sx={inputSx}
                            />
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>

            <div className="pa-footer">
              <button className="pa-btn pa-btn-secondary" onClick={onClose}>{t('cancel')}</button>
              <button className="pa-btn pa-btn-primary" onClick={confirm} disabled={importable.length === 0}>
                <FontAwesomeIcon icon={faWandMagicSparkles} /> {t('pa_confirm')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ProductAnalyzerModal;
